import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeader from "@/components/feature/SectionHeader";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import { getMediaById, getRelated, TYPE_LABEL, formatViews, formatDate } from "@/lib/media";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getMediaById(id) : undefined;
  const { isFavorite, toggle } = useFavorites();
  const [copied, setCopied] = useState(false);

  useDocumentMeta(
    item ? `${item.title} - 最近中文字幕` : "影视详情 - 最近中文字幕",
    item
      ? `${item.title}：${item.summary} ${item.subtitleInfo}`
      : "影视详情页，提供中文字幕更新信息。",
    item ? `${item.title},中文字幕,${item.genres.join(",")},影视简介` : "中文字幕,影视详情"
  );

  if (!item) {
    return <NotFound />;
  }

  const favorited = isFavorite(item.id);
  const related = getRelated(item, 4);
  const infoRows = [
    { label: "类别", value: TYPE_LABEL[item.type], icon: item.type === "movie" ? "ri-film-line" : "ri-tv-2-line" },
    { label: "年代", value: `${item.year} 年`, icon: "ri-calendar-line" },
    { label: "地区", value: item.region, icon: "ri-map-pin-line" },
    { label: "导演", value: item.director, icon: "ri-user-star-line" },
    { label: "类型", value: item.genres.join(" / "), icon: "ri-price-tag-3-line" },
    { label: "主演", value: item.cast.join("、"), icon: "ri-group-line" },
  ];

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // 剪贴板不可用时忽略
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="animate-fade-up">
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-5 md:px-6 md:py-6">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400">
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${item.type}`} className="hover:text-primary-600">
              {TYPE_LABEL[item.type]}专区
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{item.title}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr] lg:gap-10">
          <div className="mx-auto w-full max-w-[280px] lg:mx-0 lg:max-w-none">
            <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={item.cover}
                alt={`${item.title} 封面海报`}
                title={`${item.title} 中文字幕资源`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                {TYPE_LABEL[item.type]}
              </span>
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                <i className="ri-closed-captioning-line"></i>
                中文字幕已更新
              </span>
              <span className="flex items-center gap-1 rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                <i className="ri-star-fill text-accent-500"></i>
                {item.rating} 分
              </span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {item.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line text-accent-500"></i>
                {formatViews(item.views)} 次浏览
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line text-primary-500"></i>
                更新于 {formatDate(item.updatedAt)}
              </span>
            </div>

            <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex items-start gap-2 text-sm">
                  <dt className="flex shrink-0 items-center gap-1.5 text-foreground-400">
                    <i className={row.icon}></i>
                    {row.label}
                  </dt>
                  <dd className="text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-6 flex flex-wrap gap-2">
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => toggle(item.id)}
                aria-pressed={favorited}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-5 py-3 text-sm font-medium transition-colors ${
                  favorited
                    ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className={copied ? "ri-check-line" : "ri-share-forward-line"}></i>
                {copied ? "链接已复制" : "分享"}
              </button>
              <Link
                to={`/category/${item.type}`}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-closed-captioning-line"></i>
                更多{TYPE_LABEL[item.type]}字幕
              </Link>
            </div>
          </div>
        </div>

        <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeader title="剧情简介" icon="ri-file-text-line" />
            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
                {item.summary}
              </p>
              <h3 className="mt-6 flex items-center gap-1.5 text-sm font-bold text-foreground-900">
                <i className="ri-closed-captioning-line text-accent-600"></i>
                中文字幕信息
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {item.subtitleInfo}
              </p>
            </div>
          </div>

          <div>
            <SectionHeader title="收藏提示" icon="ri-bookmark-3-line" />
            <div className="mt-4 rounded-lg border border-background-200 bg-secondary-50 p-5">
              <p className="text-sm leading-relaxed text-secondary-900">
                收藏功能使用浏览器本地存储保存，仅对当前设备与浏览器生效。清理浏览器数据或更换设备后收藏记录将无法同步。
              </p>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-foreground-500">
                <i className="ri-shield-check-line"></i>
                本站不收集个人账号信息
              </p>
            </div>
          </div>
        </section>

        {item.type === "tv" && item.episodes && item.episodes.length > 0 && (
          <section className="mt-12">
            <SectionHeader
              title="剧集列表"
              subtitle={`共 ${item.episodes.length} 集，点击查看每集简介（演示内容，无真实播放）`}
              icon="ri-play-list-line"
            />
            <div className="mt-5">
              <EpisodeList episodes={item.episodes} />
            </div>
          </section>
        )}

        <section className="mt-12">
          <SectionHeader
            title="相关推荐"
            subtitle="根据类型与标签为你推荐相似的中文字幕作品"
            icon="ri-magic-line"
          />
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {related.map((relatedItem) => (
              <MovieCard key={relatedItem.id} item={relatedItem} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}